// Chart / axis formatters

/**
 * Compact magnitude with thousand / million / billion markers.
 * Examples: `984` → `984`, `1141` → `1.1K`, `1_250_000` → `1.3M`, `-2_000_000_000` → `-2B`.
 */
export function formatMagnitude(value: number, fractionDigits = 1): string {
  if (!Number.isFinite(value)) return '-';
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `${sign}${trimMagnitude(abs / 1_000_000_000, fractionDigits)}B`;
  if (abs >= 1_000_000) return `${sign}${trimMagnitude(abs / 1_000_000, fractionDigits)}M`;
  if (abs >= 1_000) return `${sign}${trimMagnitude(abs / 1_000, fractionDigits)}K`;
  return `${sign}${new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(abs)}`;
}

function trimMagnitude(n: number, fractionDigits: number): string {
  const fixed = n.toFixed(fractionDigits);
  return fixed.replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
}

/** Axis / metric ticks — prefer K/M/B markers for |value| ≥ 1000. */
export function formatMetric(value: number): string {
  return formatMagnitude(value);
}

export function formatMonthYear(value: string | number | Date): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(date);
}

/** Axis / label number with optional suffix (e.g. `%`). Uses K/M/B when |n| ≥ 1000. */
export function formatAxisValue(value: string | number, suffix?: string): string {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  const body = Math.abs(n) >= 1000 ? formatMagnitude(n) : Number.isInteger(n) ? String(n) : n.toFixed(1);
  return suffix ? `${body}${suffix}` : body;
}

/** Best-effort display string for an arbitrary cell value (Date → ISO, non-primitives → ''). */
export function toCellString(value: unknown): string {
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') return String(value);
  return '';
}

// Financial cell formatters (null / undefined / blank → em dash)

/** Em dash used for null / undefined / blank financial cells (MVP tearsheet). */
export const EM_DASH = '—';

function isBlank(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

function toFiniteNumber(value: unknown): number | null {
  if (isBlank(value)) return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Format a number as `12.34%` (null → em dash). Values are already in percent units (e.g. 3.58 → `3.58%`). */
export function formatPercent(value: unknown, fractionDigits = 2): string {
  const n = toFiniteNumber(value);
  if (n == null) return EM_DASH;
  return `${n.toFixed(fractionDigits)}%`;
}

/** Format a plain number with fixed fraction digits (null → em dash). */
export function formatNumber(value: unknown, fractionDigits = 2): string {
  const n = toFiniteNumber(value);
  if (n == null) return EM_DASH;
  return n.toFixed(fractionDigits);
}

/** Format a ratio to 4 decimal places by default (null → em dash). */
export function formatRatio(value: unknown, fractionDigits = 4): string {
  return formatNumber(value, fractionDigits);
}

export type CurrencyFormatOptions = {
  currency?: string;
  fractionDigits?: number;
  locale?: string;
};

/** Format a currency amount with grouping (null → em dash). Defaults to USD / en-US. */
export function formatCurrency(value: unknown, options: CurrencyFormatOptions = {}): string {
  const n = toFiniteNumber(value);
  if (n == null) return EM_DASH;
  const { currency = 'USD', fractionDigits = 2, locale = 'en-US' } = options;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(n);
}

/** Date display styles for table cells. */
export type DateStyle = 'dd/mm/yyyy' | 'mm/dd/yyyy' | 'medium' | 'long';

function toValidDate(value: unknown): Date | null {
  if (isBlank(value)) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const date = new Date(value as string | number);
  return Number.isNaN(date.getTime()) ? null : date;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * Format a date for table display (null / invalid → em dash).
 * - `dd/mm/yyyy` → `12/03/2026`
 * - `mm/dd/yyyy` → `03/12/2026`
 * - `medium` → `12 Mar 2026` (abbreviated month)
 * - `long` → `12 March 2026` (written month)
 */
export function formatDate(value: unknown, style: DateStyle = 'dd/mm/yyyy'): string {
  const date = toValidDate(value);
  if (!date) return EM_DASH;

  const day = date.getUTCDate();
  const month = date.getUTCMonth() + 1;
  const year = date.getUTCFullYear();

  if (style === 'dd/mm/yyyy') return `${pad2(day)}/${pad2(month)}/${year}`;
  if (style === 'mm/dd/yyyy') return `${pad2(month)}/${pad2(day)}/${year}`;

  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: style === 'long' ? 'long' : 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

const shortMonthFormat = new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' });
const longMonthFormat = new Intl.DateTimeFormat('en-US', { month: 'long', timeZone: 'UTC' });
const utcClockFormat = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
  timeZone: 'UTC',
});

function monthDate(monthIndex: number): Date {
  return new Date(Date.UTC(2020, monthIndex, 1));
}

/** `Jan` … `Dec`. `monthIndex` is 0–11. */
export function shortMonthName(monthIndex: number): string {
  return shortMonthFormat.format(monthDate(monthIndex));
}

/** `January` … `December`. `monthIndex` is 0–11. */
export function longMonthName(monthIndex: number): string {
  return longMonthFormat.format(monthDate(monthIndex));
}

export const SHORT_MONTH_LABELS: readonly string[] = Array.from({ length: 12 }, (_, index) => shortMonthName(index));

function utcClock(date: Date): string {
  return utcClockFormat.format(date);
}

/** `15 Sep 2026, 11:42:18` in UTC. Invalid values pass through as text. */
export function formatUtcDateTime(value: unknown): string {
  const date = toValidDate(value);
  if (!date) return isBlank(value) ? EM_DASH : String(value);
  return `${date.getUTCDate()} ${shortMonthName(date.getUTCMonth())} ${date.getUTCFullYear()}, ${utcClock(date)}`;
}

/** `15 Sep 11:42:18` in UTC. Invalid values pass through as text. */
export function formatUtcDayTime(value: unknown): string {
  const date = toValidDate(value);
  if (!date) return isBlank(value) ? EM_DASH : String(value);
  return `${date.getUTCDate()} ${shortMonthName(date.getUTCMonth())} ${utcClock(date)}`;
}

/**
 * Apply a simple digit mask (`#` = next digit). Extra digits are ignored;
 * missing digits leave remaining `#` slots empty.
 * Example: `applyMask('4155550134', '(###) ###-####')` → `(415) 555-0134`
 */
export function applyMask(value: unknown, mask: string): string {
  if (isBlank(value)) return EM_DASH;
  const digits = String(value).replace(/\D/g, '');
  if (!digits) return EM_DASH;

  let di = 0;
  let out = '';
  for (const ch of mask) {
    if (di >= digits.length) break;
    if (ch === '#') out += digits[di++];
    else out += ch;
  }
  return out || EM_DASH;
}
